import { ViewConfig } from "@lib/views";
import { DATE_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import {
    AuditLogChangeType,
    AuditLogDiff,
    HistoryDetailsItem,
    ResourceType,
} from "@common/types/auditLog";
import { HistoryDetailsDateCell } from "../../cells/HistoryDetailsDateCell";
import { HistoryFinancialAccountNameCell } from "../cells/HistoryFinancialAccountNameCell";
import {
    HistoryFinancialAccountNewValueCell,
    HistoryFinancialAccountOldValueCell,
} from "../cells/HistoryFinancialAccountValueCells";
import { HistoryDetailsBaseRow } from "../../cells/historyDetailsBaseRow";
import { HistoryFinancialAccountDetailsColumn } from "./historyFinancialAccountDetailsView";

export interface HistoryFinancialAccountDetailsRow extends HistoryDetailsBaseRow {
    id: string;
    changeType: AuditLogChangeType;
    resourceType: ResourceType;
    resourceId: string;
    resource?: Record<string, unknown>;
    accountName?: string;
    field: string;
    fieldLabel: string;
    oldValue: AuditLogDiff["before"];
    newValue: AuditLogDiff["after"];
}

const FINANCIAL_ACCOUNT_DISPLAYABLE_FIELDS: Record<string, string> = {
    name: "Account Name",
    entityId: "Entity",
    institutionName: "Institution",
    accountType: "Account Type",
    accountSubtype: "Account Subtype",
    currency: "Currency",
    balance: "Balance",
    availableBalance: "Available Balance",
    creditLimit: "Credit Limit",
    status: "Status",
    lastSync: "Last Sync",
    accountNumber: "Account Number",
    routingNumber: "Routing Number",
    isActive: "Active",
};

export const getFinancialAccountFieldLabel = (
    field: string,
): string | undefined => FINANCIAL_ACCOUNT_DISPLAYABLE_FIELDS[field];

export const filterFinancialAccountDisplayableDiffs = (
    diffs: HistoryDetailsItem["diff"],
): AuditLogDiff[] =>
    diffs.filter((diff: AuditLogDiff) => {
        const fieldLabel = getFinancialAccountFieldLabel(diff.field);
        if (!fieldLabel) {
            return false;
        }
        const hasLabel = diff.before.valueLabel ?? diff.after.valueLabel;
        const hasValue =
            diff.before.value !== null || diff.after.value !== null;
        return hasLabel ?? hasValue;
    });

export const historyFinancialAccountDetailsViewConfig: ViewConfig<
    HistoryFinancialAccountDetailsRow,
    HistoryFinancialAccountDetailsColumn,
    never
> = {
    columns: {
        [HistoryFinancialAccountDetailsColumn.DATE]: {
            name: "Date",
            header: "Date",
            accessorKey: "date",
            cell: HistoryDetailsDateCell,
            ...DATE_COLUMN_SIZE,
        },
        [HistoryFinancialAccountDetailsColumn.ACCOUNT_NAME]: {
            name: "Account",
            header: "Account",
            accessorKey: "accountName",
            cell: HistoryFinancialAccountNameCell,
            minSize: 250,
            maxSize: 300,
        },
        [HistoryFinancialAccountDetailsColumn.OLD_VALUE]: {
            name: "Old Value",
            header: "Old Value",
            accessorKey: "oldValue",
            cell: HistoryFinancialAccountOldValueCell,
            minSize: 200,
            maxSize: 250,
        },
        [HistoryFinancialAccountDetailsColumn.NEW_VALUE]: {
            name: "New Value",
            header: "New Value",
            accessorKey: "newValue",
            cell: HistoryFinancialAccountNewValueCell,
            minSize: 200,
            maxSize: 250,
        },
    },
};
